import { useState, useMemo, useReducer, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { useDebouncedCallback } from 'use-debounce';
import { FaCircle } from 'react-icons/fa';
import { MdOutlineFileDownload } from 'react-icons/md';
import getCompanyOptionsByIdsApi from '@/api/get/company/company-product/get-preview-options';
import getSupplierUEImageApi from '@/api/post/dealer/get-ue-images';
import getSupplierProductCategoriesApi from '@/api/get/company/sales-flow/get-supplier-product-categories';
import { useMutation, useQuery, useLazyQuery } from '@/hooks/use-api';
import { ProductOptionsProvider } from '@/pages/sales-flow/components/configuration/config-context';
import {
  recursiveFlatMap,
  recursiveSetOptionOrder,
} from '@/pages/company-management/company-products/utils';
import { PrimaryOutlineButton } from '@/components/button/button-component';
import Loader from '@/components/loader/loader-component';
import { Heading2Text, SubheaderText } from '@/utils/global-fonts';
import Styled from '@/pages/sales-flow/components/configuration/configuration-style';
import RenderOption from '@/pages/sales-flow/components/configuration/render-options-v2';
import ImageCarousel from '@/components/image-carousel/image-carousel-component';
import getDealerProductApi from '@/api/get/company/dealer-product/get-dealer-product';
import { ProductContextProvider } from './context-provider';

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_SHOW_DEPENDENCIES': {
      const { node } = action.payload;
      if (state.queue.length === 0) {
        return {
          ...state,
          show: action.payload,
          node,
          queue: [],
        };
      }
      const id = `${node.id}_${node.part_number}`;
      const el = document.getElementById(id);

      const found = state.queue.find((x) => x.id === id);

      if (!found) {
        return {
          ...state,
          show: action.payload,
          node,
        };
      }
      return {
        ...state,
        queue: [...state.queue, el],
      };
    }
    case 'ADD': {
      const found = state.queue.find((x) => x.id === action.payload.id);

      if (!found) {
        return {
          ...state,
          queue: [...state.queue, action.payload],
        };
      }
      return state;
    }
    case 'DONE': {
      if (state.queue.length === 0) {
        return {
          ...state,
          node: null,
          show: null,
        };
      }
      if (state.queue[0]) {
        state.queue[0].click();
      }
      const newQueue = state.queue;
      newQueue.shift();
      return {
        ...state,
        node: null,
        show: null,
        queue: newQueue,
      };
    }
    default: {
      return state;
    }
  }
};

/**
 * Preview product component.
 * @returns {JSX.Element} The rendered Product Preview page component.
 */
const ProductPreview = () => {
  const [slideIndex, setSlideIndex] = useState(0);
  const { id, productId } = useParams();
  const { state: locationState } = useLocation();
  const [prices, setPrices] = useState([]);
  const [options, setOptions] = useState([]);
  const [ueImages, setUeImages] = useState([]);
  const [ueOnline, setUeOnline] = useState(true);
  const [expandedOption, setExpandedOption] = useState(null);
  const [ueInitialLoad, setInitialLoad] = useState(true);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const [showDependencies, setShowDependencies] = useReducer(reducer, {
    show: false,
    node: null,
    queue: [],
  });

  const getApi = useRef(getCompanyOptionsByIdsApi(id));

  const { refetch } = useLazyQuery(getApi.current);

  const getProductFormValues = () => {
    setLoading(true);
    const getOptions = (productFormValues) => {
      const optionIds = recursiveFlatMap(
        productFormValues?.productOptions,
        [],
        true,
      )?.map((x) => x.option_price_link_id);

      const dependencyOptionIds = recursiveFlatMap(
        productFormValues?.productOptions,
        [],
        true,
      )
        ?.map((x) => x.dependencies)
        .flat();
      optionIds.push(...dependencyOptionIds);

      refetch(
        [],
        optionIds.filter((x) => x !== null),
      )
        .then((res) => {
          const tempOptions = productFormValues?.productOptions?.map(
            (option) => {
              const optionData = res?.data?.find(
                (x) =>
                  x.id === option.option_id ||
                  x.option_price_link_id === option.option_price_link_id,
              );

              if (option?.children) {
                const updateChildrenRecursively = (children) =>
                  children.map((child) => {
                    const childData = res?.data?.filter((x) => {
                      if (child?.isVariant) {
                        if (
                          x.id === child.option_id ||
                          x.option_price_link_id === child.option_price_link_id
                        ) {
                          if (
                            (x.is_default === false &&
                              x.parent_option !== null &&
                              x.option_price_link_id ===
                                child.option_price_link_id) ||
                            (x.parent_option === null &&
                              x.is_default &&
                              x.option_price_link_id ===
                                child.option_price_link_id)
                          ) {
                            return true;
                          }
                          return false;
                        }
                        return false;
                      }
                      return (
                        x.id === child.option_id ||
                        x.option_price_link_id === child.option_price_link_id
                      );
                    });

                    let dependenciesPartNumbers = [];
                    if (child?.dependencies?.length > 0) {
                      dependenciesPartNumbers = child.dependencies.map(
                        (x) =>
                          res.data.find((y) => y.option_price_link_id === x)
                            ?.part_number,
                      );
                    }

                    return {
                      ...child,
                      thumbnail: childData[0]?.thumbnail,
                      part_number: childData[0]?.part_number,
                      dependenciesPartNumbers,
                      option_price_link_thumbnail:
                        childData[0]?.option_price_link_thumbnail,
                      children: child.children
                        ? updateChildrenRecursively(child.children)
                        : undefined,
                    };
                  });

                return {
                  ...option,
                  thumbnail: optionData?.thumbnail,
                  part_number: optionData?.part_number,
                  dependenciesPartNumbers: optionData?.dependenciesPartNumbers,
                  option_price_link_thumbnail:
                    optionData?.option_price_link_thumbnail,
                  children: updateChildrenRecursively(option.children),
                };
              }

              return {
                ...option,
                option_id: option?.option_id ?? optionData?.id,
                part_number: optionData?.part_number,
                dependenciesPartNumbers: optionData?.dependenciesPartNumbers,
                option_price_link_thumbnail:
                  optionData?.option_price_link_thumbnail,
                thumbnail: optionData?.thumbnail,
              };
            },
          );

          setData({ ...productFormValues, productOptions: tempOptions });
          setLoading(false);
        })
        .catch(() => setLoading(false));
    };

    if (locationState?.fromTable) {
      // if the preview is from the table we need to get the product first from the api and then get the options.
      getDealerProductApi(id, productId).then((res) => {
        const productFormValues = res.data;
        productFormValues.productOptions = recursiveSetOptionOrder(
          productFormValues?.productOptions ?? [],
        );
        getOptions(productFormValues);
      });
    } else {
      const productFormValues = JSON.parse(
        localStorage.getItem('productFormValues'),
      );
      getOptions(productFormValues);
    }
  };

  useEffect(() => {
    getProductFormValues();
  }, []);

  const { data: productCategories, loading: productCategoriesLoading } =
    useQuery(getSupplierProductCategoriesApi, id);
  const [ueImage, { error }] = useMutation(getSupplierUEImageApi);

  const methods = useForm({
    mode: 'onSubmit',
  });
  const { watch, getValues } = methods;
  const watchedValues = watch();

  const processNodes = (nodes) => {
    const filteredNodes = nodes
      ?.filter(
        (node) =>
          (node.id !== undefined && node.id !== null) ||
          node.belowTheLine === true,
      )
      ?.map((node) => ({
        product_option_link_id: node.id ?? node.node,
        below_the_line:
          node.belowTheLine !== undefined ? node.belowTheLine : false,
      }));
    return filteredNodes;
  };

  const isEmpty = useMemo(
    () =>
      processNodes(recursiveFlatMap(watchedValues?.options ?? []))?.length ===
      0,
    [watchedValues],
  );

  const contextValue = {
    prices,
    options,
    setOptions,
    setPrices,
  };

  const overrideUnrealImages = useMemo(
    () => data?.product?.override_unreal_images ?? false,
    [data?.product?.override_unreal_images],
  );

  const fetchUEImages = useDebouncedCallback(() => {
    if (!overrideUnrealImages && !showDependencies.show) {
      const watchedOptionsValues = getValues('options');
      const flattenRootOptions = recursiveFlatMap(
        data?.productOptions ?? [],
        [],
        true,
      );
      // Function to collect all dependencies in the tree
      const getDependencies = (node) => {
        let result = [];
        if (node.id) {
          const rootOption = flattenRootOptions.find((x) => x.id === node.id);
          if (rootOption?.dependenciesPartNumbers?.length > 0) {
            result.push(rootOption.dependenciesPartNumbers);
          }
          if (node.children) {
            node.children.forEach((child) => {
              result = result.concat(getDependencies(child));
            });
          }
        }
        return result.flat();
      };

      // Function to flatten the tree and collect part numbers
      const flattenTree = (node) => {
        let result = [];
        if (node.partNumber && node.type && node.id) {
          result.push(node.partNumber);
        }
        if (node.children) {
          node.children.forEach((child) => {
            if (child?.id) {
              result = result.concat(flattenTree(child));
            }
          });
        }
        return result;
      };

      // Get all dependencies in the watchedOptionsValues
      const allDependencies = watchedOptionsValues.flatMap(getDependencies);

      // Extract part numbers from matchedNodes

      // Flatten the tree to collect all part numbers
      const flattenedPartNumbers = watchedOptionsValues.flatMap(flattenTree);

      // Combine part numbers from the tree and dependencies
      const combinedPartNumbers = flattenedPartNumbers.concat(allDependencies);
      // Remove duplicates by converting to a Set and back to an array
      const uniquePartNumbers = [...new Set(combinedPartNumbers)];

      if (uniquePartNumbers.length > 0) {
        ueImage(data?.product?.id, uniquePartNumbers)
          .then((res) => {
            setUeImages(res.data);
            setUeOnline(true);
          })
          .catch(() => setUeOnline(false));
      }
      if (uniquePartNumbers.length === 0 || isEmpty) {
        ueImage(data?.product?.id, [])
          .then((res) => {
            setUeImages(res.data);
            setUeOnline(true);
          })
          .catch(() => setUeOnline(false));
      }
    }
  }, 200);

  const hasFetchedUEImages = useRef(false);

  useEffect(() => {
    if (
      hasFetchedUEImages.current ||
      overrideUnrealImages === undefined ||
      !data?.product?.id
    ) {
      return;
    }
    hasFetchedUEImages.current = true;
    ueImage(data?.product?.id, [])
      .then((res) => {
        setUeImages(res.data);
        setUeOnline(true || Object.values(error).length > 0);
      })
      .catch(() => setUeOnline(false))
      .finally(() => setInitialLoad(false));
  }, [overrideUnrealImages, data?.product?.id, ueImage, error]);

  const selectedProductCategory = useMemo(() => {
    const productCategory = productCategories?.find(
      (category) => category.id === data?.product?.product_category_id,
    );

    return productCategory?.name ?? '';
  }, [data, productCategories]);

  if (loading || productCategoriesLoading) {
    return <Loader />;
  }

  return (
    <ProductContextProvider value={contextValue}>
      <ProductOptionsProvider
        productOptions={data?.productOptions}
        overrideUnrealImages={data?.product.override_unreal_images}
        fetchUEImagesFunc={fetchUEImages}
        ueImages={ueImages}
        showDependencies={showDependencies}
        setShowDependencies={setShowDependencies}
      >
        <FormProvider {...methods}>
          <Styled.WrapperContainer>
            <Styled.WrapperChild>
              <Styled.MainContainer>
                <Styled.SubContainer>
                  <Styled.LeftContainer>
                    <Styled.HeadingMainContainer>
                      <Styled.HeadingContainer>
                        <Heading2Text>{data?.product?.name}</Heading2Text>
                        <Styled.ConfigurationContainer>
                          <SubheaderText>{`${selectedProductCategory} - ${data?.product?.name}`}</SubheaderText>
                        </Styled.ConfigurationContainer>
                      </Styled.HeadingContainer>
                      <FaCircle fill={ueOnline ? 'lightgreen' : 'red'} />
                    </Styled.HeadingMainContainer>
                    <Styled.CarouselContainer>
                      <ImageCarousel
                        images={
                          !overrideUnrealImages && ueImages?.length > 0
                            ? ueImages
                            : data?.productGallery
                        }
                        startIndex={slideIndex}
                        onSlide={(x) => setSlideIndex(x)}
                        loading={ueImages.length === 0 && ueInitialLoad}
                      />
                      <Styled.ButtonContainer>
                        {data?.productDocuments?.map((item) => (
                          <PrimaryOutlineButton
                            key={item.id}
                            title={item.name}
                            margin="0 0 10px 10px"
                            icon={<MdOutlineFileDownload />}
                            type="button"
                            onClick={() => window.open(item.document, '_blank')}
                          />
                        ))}
                      </Styled.ButtonContainer>
                    </Styled.CarouselContainer>
                  </Styled.LeftContainer>
                  <Styled.MainOptionsContainer>
                    {data?.productOptions
                      ?.filter((option) => !option?.is_hidden)
                      ?.map((option, index) => (
                        <RenderOption
                          key={option.id}
                          node={option}
                          expandedParentOption={expandedOption === index}
                          setExpandedParentOption={() => {
                            if (expandedOption !== index) {
                              setExpandedOption(index);
                            } else {
                              setExpandedOption(null);
                            }
                          }}
                          companyId={id}
                          name={`options.${index}`}
                          parentName="options"
                          level={0}
                          parentNode={option}
                          isFirst={index === 0}
                          partNumber={
                            option.option_type === 'folder'
                              ? option.name
                              : option.part_number
                          }
                        />
                      ))}
                  </Styled.MainOptionsContainer>
                </Styled.SubContainer>
              </Styled.MainContainer>
            </Styled.WrapperChild>
          </Styled.WrapperContainer>
        </FormProvider>
      </ProductOptionsProvider>
      {/* </FormWrapper> */}
    </ProductContextProvider>
  );
};

export default ProductPreview;
